import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Calculator,
  Loader2,
  FileText,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  getCompanyName,
} from "../lib/textron-data-processor";
import React, { useState, useEffect } from "react";

// DD/MM/YY or DD/MM/YYYY (1–2 digit day/month allowed). 4-digit years must not use 1900+year.
const parseDate = (dateStr: string): Date => {
  const parts = dateStr.trim().split("/");
  if (parts.length !== 3) return new Date(NaN);
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const yStr = parts[2].trim();
  let fullYear = parseInt(yStr, 10);
  if (yStr.length === 2) {
    fullYear = fullYear < 50 ? 2000 + fullYear : 1900 + fullYear;
  }
  if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(fullYear)) return new Date(NaN);
  return new Date(fullYear, month - 1, day);
};

const compareDateKeysDesc = (a: string, b: string): number => {
  const ta = parseDate(a).getTime();
  const tb = parseDate(b).getTime();
  const na = Number.isNaN(ta);
  const nb = Number.isNaN(tb);
  if (na && nb) return a.localeCompare(b);
  if (na) return 1;
  if (nb) return -1;
  return tb - ta;
};

// Helper function to find all dates from JSON keys (latest first)
const findAllDates = (data: any): string[] => {
  const dates = Object.keys(data).filter((key) =>
    /^\d{1,2}\/\d{1,2}\/(\d{2}|\d{4})$/.test(key)
  );

  if (dates.length === 0) return [];

  dates.sort(compareDateKeysDesc);
  return dates;
};

/** Oldest period first — use for charts/sparklines instead of assuming `findAllDates` order. */
const sortDatesOldestFirst = (dateKeys: string[]): string[] =>
  [...dateKeys].sort((a, b) => {
    const ta = parseDate(a).getTime();
    const tb = parseDate(b).getTime();
    const na = Number.isNaN(ta);
    const nb = Number.isNaN(tb);
    if (na && nb) return a.localeCompare(b);
    if (na) return 1;
    if (nb) return -1;
    const t = ta - tb;
    return t !== 0 ? t : a.localeCompare(b);
  });

/** Top charts: enforce chronological order (oldest → newest) regardless of JSON key order. */
function sortChartRowsByDate<T extends { date: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ta = parseDate(a.date).getTime();
    const tb = parseDate(b.date).getTime();
    const na = Number.isNaN(ta);
    const nb = Number.isNaN(tb);
    if (na && nb) return a.date.localeCompare(b.date);
    if (na) return 1;
    if (nb) return -1;
    const t = ta - tb;
    return t !== 0 ? t : a.date.localeCompare(b.date);
  });
}

// Helper function to format value in crores
const formatCrores = (value: number): string => {
  const crores = value / 10000000;
  return `₹${Math.abs(crores).toFixed(2)} Cr`;
};

// Sparkline: plot trend series (oldest→newest, left to right). Prefer explicit points so QE/YE and order match trend.
function TrendSparkline({
  points: pointsProp,
  values,
  dates,
  trend,
  datesAreChronological,
  isExpense,
}: {
  points?: number[];
  values?: { [date: string]: number };
  dates?: string[];
  trend: string | null | undefined;
  datesAreChronological?: boolean;
  /** Expense: uptrend red, downtrend green. Income: uptrend green, downtrend red. */
  isExpense?: boolean;
}) {
  const points =
    pointsProp ??
    (dates && values
      ? (datesAreChronological ? dates : [...dates].reverse()).map((d) => values[d]).filter((v): v is number => typeof v === "number")
      : []);
  if (points.length < 2) return <span className="text-white/50">—</span>;

  const w = 56;
  const h = 20;
  const padding = 2;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const xStep = (w - padding * 2) / (points.length - 1);

  const pathD = points
    .map((val, i) => {
      const x = padding + i * xStep;
      const y = h - padding - ((val - min) / range) * (h - padding * 2);
      return `${i === 0 ? "M" : "L"} ${x} ${y}`;
    })
    .join(" ");

  const stroke =
    trend === "Stable"
      ? "rgba(255,255,255,0.9)"
      : isExpense
        ? trend === "Up"
          ? "#ef4444"
          : "#22c55e"
        : trend === "Up"
          ? "#22c55e"
          : "#ef4444";

  const uid = React.useId().replace(/:/g, "");
  const filterId = `sparkline-glow-${uid}`;
  const markerId = `sparkline-arrow-${uid}`;

  return (
    <svg width={w} height={h} className="inline-block" aria-hidden>
      <defs>
        <filter id={filterId} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <marker
          id={markerId}
          markerUnits="userSpaceOnUse"
          markerWidth="8"
          markerHeight="6"
          refX="6"
          refY="3"
          orient="auto"
        >
          <path d="M 0 0 L 6 3 L 0 6 Z" fill={stroke} />
        </marker>
      </defs>
      <g filter={`url(#${filterId})`}>
        <path
          d={pathD}
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          markerEnd={`url(#${markerId})`}
        />
      </g>
    </svg>
  );
}

// True when every date key is 31/03/YY (year-end only)
const hasOnlyMarch31Dates = (dateKeys: string[]): boolean =>
  dateKeys.length > 0 && dateKeys.every((k) => /^31\/03\/(\d{2}|\d{4})$/.test(k));

export type DateColumn = { type: "QE" | "YE"; date: string };

type ProfitLossDateEntry = {
  pl_as?: { [key: string]: number };
  pl_as_ye?: { [key: string]: number };
  pl_as_qe?: { [key: string]: number };
  formulae?: { Net_profit_ratio?: number };
};
function getDateEntry(data: ProfitLossData, date: string): ProfitLossDateEntry | undefined {
  const v = data[date];
  return v && "pl_as_qe" in v ? (v as ProfitLossDateEntry) : undefined;
}
interface ProfitLossData {
  [key: string]:
    | ProfitLossDateEntry
    | { pl?: { cagr?: { [key: string]: number | null }; trend?: { [key: string]: string | null }; correlation_rc?: number | null } }
    | undefined;
  analytics?: {
    pl?: {
      cagr?: { [key: string]: number | null };
      trend?: { [key: string]: string | null };
      correlation_rc?: number | null;
      "correlation_profit-mp"?: number | null;
      "correlation_revenue-mp"?: number | null;
    };
  };
}

interface PLItem {
  label: string;
  key?: string;
  values: { [date: string]: number };
  /** When mixed (non–31/03) dates exist: quarter-end figures per date */
  valuesQe?: { [date: string]: number };
  level: number;
  isTotal?: boolean;
  isSubItem?: boolean;
  isSectionHeader?: boolean;
  /** True for expense items (uptrend = red, downtrend = green); false for income/profit (uptrend = green, downtrend = red) */
  isExpense?: boolean;
  cagr?: number | null;
  trend?: string | null;
}

interface ProfitLossSectionProps {
  searchTerm: string;
}

// ── Ind AS P&L row types (same pattern as balance-sheet Ind AS) ──────────────
type IndASPLRowType =
  | "sectionHeader"
  | "subGroupHeader"
  | "lineItem"
  | "subTotal"
  | "grandTotal"
  | "blank";

interface IndASPLRow {
  name: string;
  key?: string;
  type: IndASPLRowType;
  level: number;
  values: { [date: string]: number };
}

const formatVariancePct = (v1: number, v2: number): string => {
  if (v2 === 0) return v1 === 0 ? "0.00%" : "N/A";
  const pct = ((v1 - v2) / Math.abs(v2)) * 100;
  return `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`;
};

export function ProfitLossSection({ searchTerm }: ProfitLossSectionProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [companyName, setCompanyName] = useState<string>("");
  const [frf, setFrf] = useState<string>("");
  const [dates, setDates] = useState<string[]>([]);
  const [dateColumns, setDateColumns] = useState<DateColumn[]>([]);
  const [showOnlyYe, setShowOnlyYe] = useState(true);
  const [plData, setPlData] = useState<PLItem[]>([]);
  // Ind AS-specific state
  const [indAsDates, setIndAsDates] = useState<string[]>([]);
  const [indAsPlRows, setIndAsPlRows] = useState<IndASPLRow[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsMessage, setAnalyticsMessage] = useState<string | null>(null);
  // Key: "date|type" e.g. "30/06/25|YE". Stored as string for input display.
  const [noOfShares, setNoOfShares] = useState<Record<string, string>>({});
  const [eps, setEps] = useState<Record<string, string>>({});
  const [mps, setMps] = useState<Record<string, string>>({});
  const [netProfitRatioChartData, setNetProfitRatioChartData] = useState<
    { date: string; sortKey: number; value: number }[]
  >([]);
  const [revenueExpensesScatterData, setRevenueExpensesScatterData] = useState<{ date: string; revenue: number; expenses: number; regression: number }[]>([]);
  const [correlationRc, setCorrelationRc] = useState<number | null>(null);
  const [profitMpsScatterData, setProfitMpsScatterData] = useState<{ date: string; profit: number; mps: number; regression: number }[]>([]);
  const [revenueMpsScatterData, setRevenueMpsScatterData] = useState<{ date: string; revenue: number; mps: number; regression: number }[]>([]);
  const [correlationProfitMp, setCorrelationProfitMp] = useState<number | null>(null);
  const [correlationRevenueMp, setCorrelationRevenueMp] = useState<number | null>(null);

  // Load financial data from SharePoint
  const loadFinancialData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading Profit & Loss data');

      // Get company name
      const companyNameResult = await getCompanyName("combined");
      setCompanyName(companyNameResult);

      if (!(window as any).sharePointAPI?.downloadClientJson) {
        throw new Error("SharePoint API not available");
      }

      // ── Step 1: run get_frf.py to read FRF from EngagementDetails.json ─────
      const frfResult = await (window as any).sharePointAPI.getFrf();
      const detectedFrf: string = frfResult?.frf || "";
      setFrf(detectedFrf);
      console.log("[ProfitLoss] FRF from SharePoint:", detectedFrf);

      // ── Step 2: Download FinData_Formulae.json ────────────────────────────
      if (true) {
        const result = await (window as any).sharePointAPI.downloadClientJson("FinData_Formulae.json");
        
        if (result?.success && result.data) {
          const data = result.data as ProfitLossData;
          
          // Find all dates (latest first)
          const allDates = findAllDates(data);
          if (allDates.length === 0) {
            throw new Error("No valid dates found in financial data");
          }

          // ════════════════════════════════════════════════════════════════
          // Ind AS path – full PLItem[] + charts (same rendering as AS)
          // ════════════════════════════════════════════════════════════════
          if (detectedFrf === "Indian Accounting Standards (Ind AS)") {
            const onlyMarch31 = hasOnlyMarch31Dates(allDates);
            const displayDates = allDates;
            setDates(displayDates);
            setShowOnlyYe(onlyMarch31);

            const columns: DateColumn[] = onlyMarch31
              ? displayDates.map((d) => ({ type: "YE" as const, date: d }))
              : (() => {
                  const list: DateColumn[] = [];
                  const latest = allDates[0];
                  for (const date of allDates) {
                    if (date === latest || /^31\/03\/(\d{2}|\d{4})$/.test(date)) list.push({ type: "YE" as const, date });
                    list.push({ type: "QE" as const, date });
                  }
                  return list;
                })();
            setDateColumns(columns);

            const getEntry = (d: string) => getDateEntry(data, d);
            const getValueYe = (key: string, date: string): number => {
              const byYe = getEntry(date)?.pl_as_ye?.[key];
              if (typeof byYe === "number") return byYe;
              const legacy = getEntry(date)?.pl_as?.[key];
              return typeof legacy === "number" ? legacy : 0;
            };
            const getValueYeFromJson = (key: string, date: string): number | undefined => {
              const byYe = getEntry(date)?.pl_as_ye?.[key];
              if (typeof byYe === "number") return byYe;
              const legacy = getEntry(date)?.pl_as?.[key];
              return typeof legacy === "number" ? legacy : undefined;
            };
            const getValueQeFromJson = (key: string, date: string): number | undefined => {
              const qe = getEntry(date)?.pl_as_qe?.[key];
              return typeof qe === "number" ? qe : undefined;
            };
            const getValueQe = (key: string, date: string): number => {
              const qe = getEntry(date)?.pl_as_qe?.[key];
              return typeof qe === "number" ? qe : 0;
            };
            const getValue = (key: string, date: string): number => getValueYe(key, date);

            const items: PLItem[] = [];

            // I. Revenue from Operations
            const rvOpsV: { [d: string]: number } = {};
            const rvOpsQe: { [d: string]: number } = {};
            allDates.forEach(d => { rvOpsV[d] = getValue("Revenue from Operations", d); if (!onlyMarch31) rvOpsQe[d] = getValueQe("Revenue from Operations", d); });
            items.push({ label: "I. Revenue from Operations", key: "Revenue from Operations", values: rvOpsV, valuesQe: onlyMarch31 ? undefined : rvOpsQe, level: 0, isExpense: false });

            // II. Other Income
            const otherIncV: { [d: string]: number } = {};
            const otherIncQe: { [d: string]: number } = {};
            allDates.forEach(d => { otherIncV[d] = getValue("Other Income", d); if (!onlyMarch31) otherIncQe[d] = getValueQe("Other Income", d); });
            items.push({ label: "II. Other Income", key: "Other Income", values: otherIncV, valuesQe: onlyMarch31 ? undefined : otherIncQe, level: 0, isExpense: false });

            // III. TOTAL REVENUE
            const totRevV: { [d: string]: number } = {};
            const totRevQe: { [d: string]: number } = {};
            allDates.forEach(d => {
              totRevV[d] = getValueYeFromJson("TOTAL REVENUE", d) ?? (rvOpsV[d] + otherIncV[d]);
              if (!onlyMarch31) totRevQe[d] = getValueQeFromJson("TOTAL REVENUE", d) ?? (rvOpsQe[d] + otherIncQe[d]);
            });
            items.push({ label: "III. TOTAL REVENUE", key: "TOTAL REVENUE", values: totRevV, valuesQe: onlyMarch31 ? undefined : totRevQe, level: 0, isTotal: true, isExpense: false });

            // IV. EXPENSES
            items.push({ label: "IV. EXPENSES", values: {}, level: 0, isSectionHeader: true, isExpense: true });

            const costMatV: { [d: string]: number } = {}; const costMatQe: { [d: string]: number } = {};
            allDates.forEach(d => { costMatV[d] = getValue("Cost of materials consumed", d); if (!onlyMarch31) costMatQe[d] = getValueQe("Cost of materials consumed", d); });
            items.push({ label: "Cost of materials consumed", key: "Cost of materials consumed", values: costMatV, valuesQe: onlyMarch31 ? undefined : costMatQe, level: 1, isSubItem: true, isExpense: true });

            const purchV: { [d: string]: number } = {}; const purchQe: { [d: string]: number } = {};
            allDates.forEach(d => { purchV[d] = getValue("Purchases of stock-in-trade", d); if (!onlyMarch31) purchQe[d] = getValueQe("Purchases of stock-in-trade", d); });
            items.push({ label: "Purchases of stock-in-trade", key: "Purchases of stock-in-trade", values: purchV, valuesQe: onlyMarch31 ? undefined : purchQe, level: 1, isSubItem: true, isExpense: true });

            const invChgV: { [d: string]: number } = {}; const invChgQe: { [d: string]: number } = {};
            allDates.forEach(d => {
              invChgV[d] = getValue("Changes in inventories of finished goods, work-in-progress and stock-in-trade", d);
              if (!onlyMarch31) invChgQe[d] = getValueQe("Changes in inventories of finished goods, work-in-progress and stock-in-trade", d);
            });
            items.push({ label: "Changes in inventories of finished goods, work-in-progress and stock-in-trade", key: "Changes in inventories of finished goods, work-in-progress and stock-in-trade", values: invChgV, valuesQe: onlyMarch31 ? undefined : invChgQe, level: 1, isSubItem: true, isExpense: true });

            const empBenV: { [d: string]: number } = {}; const empBenQe: { [d: string]: number } = {};
            allDates.forEach(d => { empBenV[d] = getValue("Employee benefits expense", d); if (!onlyMarch31) empBenQe[d] = getValueQe("Employee benefits expense", d); });
            items.push({ label: "Employee benefits expense", key: "Employee benefits expense", values: empBenV, valuesQe: onlyMarch31 ? undefined : empBenQe, level: 1, isSubItem: true, isExpense: true });

            const finCostV: { [d: string]: number } = {}; const finCostQe: { [d: string]: number } = {};
            allDates.forEach(d => { finCostV[d] = getValue("Finance costs", d); if (!onlyMarch31) finCostQe[d] = getValueQe("Finance costs", d); });
            items.push({ label: "Finance costs", key: "Finance costs", values: finCostV, valuesQe: onlyMarch31 ? undefined : finCostQe, level: 1, isSubItem: true, isExpense: true });

            const deprV: { [d: string]: number } = {}; const deprQe: { [d: string]: number } = {};
            allDates.forEach(d => { deprV[d] = getValue("Depreciation and amortisation expense", d); if (!onlyMarch31) deprQe[d] = getValueQe("Depreciation and amortisation expense", d); });
            items.push({ label: "Depreciation and amortisation expense", key: "Depreciation and amortisation expense", values: deprV, valuesQe: onlyMarch31 ? undefined : deprQe, level: 1, isSubItem: true, isExpense: true });

            const othExpV: { [d: string]: number } = {}; const othExpQe: { [d: string]: number } = {};
            allDates.forEach(d => { othExpV[d] = getValue("Other Expenses", d); if (!onlyMarch31) othExpQe[d] = getValueQe("Other Expenses", d); });
            items.push({ label: "Other Expenses", key: "Other Expenses", values: othExpV, valuesQe: onlyMarch31 ? undefined : othExpQe, level: 1, isSubItem: true, isExpense: true });

            // TOTAL EXPENSES
            const totExpV: { [d: string]: number } = {}; const totExpQe: { [d: string]: number } = {};
            allDates.forEach(d => {
              totExpV[d] = getValueYeFromJson("TOTAL EXPENSES", d) ?? (costMatV[d] + purchV[d] + invChgV[d] + empBenV[d] + finCostV[d] + deprV[d] + othExpV[d]);
              if (!onlyMarch31) totExpQe[d] = getValueQeFromJson("TOTAL EXPENSES", d) ?? (costMatQe[d] + purchQe[d] + invChgQe[d] + empBenQe[d] + finCostQe[d] + deprQe[d] + othExpQe[d]);
            });
            items.push({ label: "TOTAL EXPENSES", key: "TOTAL EXPENSES", values: totExpV, valuesQe: onlyMarch31 ? undefined : totExpQe, level: 0, isTotal: true, isExpense: true });

            // V. PROFIT BEFORE EXCEPTIONAL ITEMS AND TAX
            const pbExcV: { [d: string]: number } = {}; const pbExcQe: { [d: string]: number } = {};
            allDates.forEach(d => {
              pbExcV[d] = getValueYeFromJson("PROFIT BEFORE EXCEPTIONAL ITEMS AND TAX", d) ?? (totRevV[d] - totExpV[d]);
              if (!onlyMarch31) pbExcQe[d] = getValueQeFromJson("PROFIT BEFORE EXCEPTIONAL ITEMS AND TAX", d) ?? (totRevQe[d] - totExpQe[d]);
            });
            items.push({ label: "V. PROFIT BEFORE EXCEPTIONAL ITEMS AND TAX (III-IV)", key: "PROFIT BEFORE EXCEPTIONAL ITEMS AND TAX", values: pbExcV, valuesQe: onlyMarch31 ? undefined : pbExcQe, level: 0, isTotal: true, isExpense: false });

            // VI. EXCEPTIONAL ITEMS
            items.push({ label: "VI. EXCEPTIONAL ITEMS", values: {}, level: 0, isSectionHeader: true, isExpense: true });
            const excItmV: { [d: string]: number } = {}; const excItmQe: { [d: string]: number } = {};
            allDates.forEach(d => { excItmV[d] = getValue("Exceptional Items", d); if (!onlyMarch31) excItmQe[d] = getValueQe("Exceptional Items", d); });

            // VII. PROFIT BEFORE TAX
            const pbtV: { [d: string]: number } = {}; const pbtQe: { [d: string]: number } = {};
            allDates.forEach(d => {
              pbtV[d] = getValueYeFromJson("PROFIT BEFORE TAX", d) ?? (pbExcV[d] - excItmV[d]);
              if (!onlyMarch31) pbtQe[d] = getValueQeFromJson("PROFIT BEFORE TAX", d) ?? (pbExcQe[d] - excItmQe[d]);
            });
            items.push({ label: "VII. PROFIT BEFORE TAX (V-VI)", key: "PROFIT BEFORE TAX", values: pbtV, valuesQe: onlyMarch31 ? undefined : pbtQe, level: 0, isTotal: true, isExpense: false });

            // VIII. TAX EXPENSE
            items.push({ label: "VIII. TAX EXPENSE", values: {}, level: 0, isSectionHeader: true, isExpense: true });
            const curTaxV: { [d: string]: number } = {}; const curTaxQe: { [d: string]: number } = {};
            allDates.forEach(d => { curTaxV[d] = getValue("Current tax", d); if (!onlyMarch31) curTaxQe[d] = getValueQe("Current tax", d); });
            items.push({ label: "1. Current tax", key: "Current tax", values: curTaxV, valuesQe: onlyMarch31 ? undefined : curTaxQe, level: 1, isSubItem: true, isExpense: true });

            const defTaxV: { [d: string]: number } = {}; const defTaxQe: { [d: string]: number } = {};
            allDates.forEach(d => { defTaxV[d] = getValue("Deferred tax", d); if (!onlyMarch31) defTaxQe[d] = getValueQe("Deferred tax", d); });
            items.push({ label: "2. Deferred tax", key: "Deferred tax", values: defTaxV, valuesQe: onlyMarch31 ? undefined : defTaxQe, level: 1, isSubItem: true, isExpense: true });

            const totTaxV: { [d: string]: number } = {}; const totTaxQe: { [d: string]: number } = {};
            allDates.forEach(d => {
              totTaxV[d] = getValueYeFromJson("TAX EXPENSE", d) ?? (curTaxV[d] + defTaxV[d]);
              if (!onlyMarch31) totTaxQe[d] = getValueQeFromJson("TAX EXPENSE", d) ?? (curTaxQe[d] + defTaxQe[d]);
            });
            items.push({ label: "", key: "TAX EXPENSE", values: totTaxV, valuesQe: onlyMarch31 ? undefined : totTaxQe, level: 0, isTotal: true, isExpense: true });

            // IX. PROFIT/(LOSS) FOR THE PERIOD
            const plPeriodV: { [d: string]: number } = {}; const plPeriodQe: { [d: string]: number } = {};
            allDates.forEach(d => {
              plPeriodV[d] = getValueYeFromJson("PROFIT/(LOSS) FOR THE PERIOD", d) ?? (pbtV[d] - totTaxV[d]);
              if (!onlyMarch31) plPeriodQe[d] = getValueQeFromJson("PROFIT/(LOSS) FOR THE PERIOD", d) ?? (pbtQe[d] - totTaxQe[d]);
            });
            items.push({ label: "IX. PROFIT/(LOSS) FOR THE PERIOD (VII-VIII)", key: "PROFIT/(LOSS) FOR THE PERIOD", values: plPeriodV, valuesQe: onlyMarch31 ? undefined : plPeriodQe, level: 0, isTotal: true, isExpense: false });

            // X. Other Comprehensive Income
            items.push({ label: "X. Other Comprehensive Income", values: {}, level: 0, isSectionHeader: true, isExpense: false });
            const ociAiV: { [d: string]: number } = {}; const ociAiQe: { [d: string]: number } = {};
            allDates.forEach(d => { ociAiV[d] = getValue("Items that will not be reclassified to profit and loss", d); if (!onlyMarch31) ociAiQe[d] = getValueQe("Items that will not be reclassified to profit and loss", d); });
            items.push({ label: "A. (i)   Items that will not be reclassified to profit and loss", key: "Items that will not be reclassified to profit and loss", values: ociAiV, valuesQe: onlyMarch31 ? undefined : ociAiQe, level: 1, isSubItem: true, isExpense: false });

            const ociAiiV: { [d: string]: number } = {}; const ociAiiQe: { [d: string]: number } = {};
            allDates.forEach(d => { ociAiiV[d] = getValue("Income-tax relating to items that will not be reclassified to profit and loss", d); if (!onlyMarch31) ociAiiQe[d] = getValueQe("Income-tax relating to items that will not be reclassified to profit and loss", d); });
            items.push({ label: "A. (ii)  Income-tax relating to items that will not be reclassified to profit and loss", key: "Income-tax relating to items that will not be reclassified to profit and loss", values: ociAiiV, valuesQe: onlyMarch31 ? undefined : ociAiiQe, level: 1, isSubItem: true, isExpense: false });

            const ociBiV: { [d: string]: number } = {}; const ociBiQe: { [d: string]: number } = {};
            allDates.forEach(d => { ociBiV[d] = getValue("Items that will be reclassified to profit and loss", d); if (!onlyMarch31) ociBiQe[d] = getValueQe("Items that will be reclassified to profit and loss", d); });
            items.push({ label: "B. (i)   Items that will be reclassified to profit and loss", key: "Items that will be reclassified to profit and loss", values: ociBiV, valuesQe: onlyMarch31 ? undefined : ociBiQe, level: 1, isSubItem: true, isExpense: false });

            const ociBiiV: { [d: string]: number } = {}; const ociBiiQe: { [d: string]: number } = {};
            allDates.forEach(d => { ociBiiV[d] = getValue("Income-tax relating to items that will be reclassified to profit and loss", d); if (!onlyMarch31) ociBiiQe[d] = getValueQe("Income-tax relating to items that will be reclassified to profit and loss", d); });
            items.push({ label: "B. (ii)  Income-tax relating to items that will be reclassified to profit and loss", key: "Income-tax relating to items that will be reclassified to profit and loss", values: ociBiiV, valuesQe: onlyMarch31 ? undefined : ociBiiQe, level: 1, isSubItem: true, isExpense: false });

            const ociTotV: { [d: string]: number } = {}; const ociTotQe: { [d: string]: number } = {};
            allDates.forEach(d => {
              ociTotV[d] = getValueYeFromJson("Other Comprehensive Income", d) ?? (ociAiV[d] - ociAiiV[d] + ociBiV[d] - ociBiiV[d]);
              if (!onlyMarch31) ociTotQe[d] = getValueQeFromJson("Other Comprehensive Income", d) ?? (ociAiQe[d] - ociAiiQe[d] + ociBiQe[d] - ociBiiQe[d]);
            });
            items.push({ label: "", key: "Other Comprehensive Income", values: ociTotV, valuesQe: onlyMarch31 ? undefined : ociTotQe, level: 0, isTotal: true, isExpense: false });

            // XI. TOTAL COMPREHENSIVE INCOME
            const totCompV: { [d: string]: number } = {}; const totCompQe: { [d: string]: number } = {};
            allDates.forEach(d => {
              totCompV[d] = getValueYeFromJson("TOTAL COMPREHENSIVE INCOME", d) ?? (plPeriodV[d] + ociTotV[d]);
              if (!onlyMarch31) totCompQe[d] = getValueQeFromJson("TOTAL COMPREHENSIVE INCOME", d) ?? (plPeriodQe[d] + ociTotQe[d]);
            });
            items.push({ label: "XI. TOTAL COMPREHENSIVE INCOME (IX-X)", key: "TOTAL COMPREHENSIVE INCOME", values: totCompV, valuesQe: onlyMarch31 ? undefined : totCompQe, level: 0, isTotal: true, isExpense: false });

            // CAGR + trend enrichment
            const plCagrInd = data.analytics?.pl?.cagr;
            const plTrendInd = data.analytics?.pl?.trend;
            const getCagrKeyInd = (item: PLItem): string | undefined => {
              if (item.key) return item.key;
              if (item.label.includes("TOTAL REVENUE")) return "TOTAL REVENUE";
              if (item.label.includes("TOTAL EXPENSES")) return "TOTAL EXPENSES";
              if (item.label.includes("PROFIT/(LOSS) FOR THE PERIOD")) return "PROFIT/(LOSS) FOR THE PERIOD";
              if (item.label.includes("PROFIT BEFORE TAX")) return "PROFIT BEFORE TAX";
              if (item.label.includes("PROFIT BEFORE EXCEPTIONAL")) return "PROFIT BEFORE EXCEPTIONAL ITEMS AND TAX";
              if (item.label.includes("TOTAL COMPREHENSIVE INCOME")) return "TOTAL COMPREHENSIVE INCOME";
              return undefined;
            };
            const hasQeColumnsInd = columns.some((c) => c.type === "QE");
            const chronologicalOrderInd = sortDatesOldestFirst(allDates);
            items.forEach((item) => {
              const cagrKey = getCagrKeyInd(item);
              item.cagr = cagrKey && plCagrInd && plCagrInd[cagrKey] != null ? plCagrInd[cagrKey]! : null;
              if (plTrendInd && cagrKey && plTrendInd[cagrKey] != null) {
                item.trend = plTrendInd[cagrKey]!;
              } else if (!item.isSectionHeader && chronologicalOrderInd.length >= 2) {
                const trendValues = hasQeColumnsInd ? item.valuesQe : item.values;
                if (trendValues && Object.keys(trendValues).length > 0) {
                  const oldest = chronologicalOrderInd[0];
                  const newest = chronologicalOrderInd[chronologicalOrderInd.length - 1];
                  const firstVal = trendValues[oldest];
                  const lastVal = trendValues[newest];
                  if (typeof firstVal === "number" && typeof lastVal === "number") {
                    if (lastVal > firstVal) item.trend = "Up";
                    else if (lastVal < firstVal) item.trend = "Down";
                    else item.trend = "Stable";
                  }
                }
              }
            });

            // no_of_shares / eps / mps from JSON
            const initSharesInd: Record<string, string> = {};
            const initEpsInd: Record<string, string> = {};
            const initMpsInd: Record<string, string> = {};
            allDates.forEach((date) => {
              const ye = getEntry(date)?.pl_as_ye as { no_of_shares?: number; eps?: number; mps?: number } | undefined;
              if (ye) {
                const k = `${date}|YE`;
                if (ye.no_of_shares != null) initSharesInd[k] = String(ye.no_of_shares);
                if (ye.eps != null && ye.eps !== 0) initEpsInd[k] = String(ye.eps);
                if (ye.mps != null) initMpsInd[k] = String(ye.mps);
              }
              const qe = getEntry(date)?.pl_as_qe as { no_of_shares?: number; eps?: number; mps?: number } | undefined;
              if (qe) {
                const k = `${date}|QE`;
                if (qe.no_of_shares != null) initSharesInd[k] = String(qe.no_of_shares);
                if (qe.eps != null && qe.eps !== 0) initEpsInd[k] = String(qe.eps);
                if (qe.mps != null) initMpsInd[k] = String(qe.mps);
              }
            });
            setNoOfShares((prev) => ({ ...prev, ...initSharesInd }));
            setEps((prev) => ({ ...prev, ...initEpsInd }));
            setMps((prev) => ({ ...prev, ...initMpsInd }));

            // Chart data (explicit chronological order; X-axis uses sortKey so labels are not lex-sorted)
            const chronoDatesInd = sortDatesOldestFirst(allDates);
            setNetProfitRatioChartData(
              sortChartRowsByDate(
                chronoDatesInd.map((date) => ({
                  date,
                  sortKey: parseDate(date).getTime(),
                  value: getEntry(date)?.formulae?.Net_profit_ratio ?? 0,
                }))
              )
            );

            const scatterPtsInd: { date: string; revenue: number; expenses: number }[] = [];
            chronoDatesInd.forEach((date) => {
              const qe = getEntry(date)?.pl_as_qe;
              if (!qe) return;
              scatterPtsInd.push({ date, revenue: typeof qe["TOTAL REVENUE"] === "number" ? qe["TOTAL REVENUE"] : 0, expenses: typeof qe["TOTAL EXPENSES"] === "number" ? qe["TOTAL EXPENSES"] : 0 });
            });
            const validScInd = scatterPtsInd.filter((p) => p.revenue !== 0 || p.expenses !== 0);
            if (validScInd.length >= 2) {
              const xs = validScInd.map((p) => p.revenue); const ys = validScInd.map((p) => p.expenses); const n = xs.length;
              const mx = xs.reduce((a, b) => a + b, 0) / n; const my = ys.reduce((a, b) => a + b, 0) / n;
              const cov = xs.reduce((acc, x, i) => acc + (x - mx) * (ys[i] - my), 0) / n;
              const vx = xs.reduce((acc, x) => acc + (x - mx) ** 2, 0) / n;
              const b = vx !== 0 ? cov / vx : 0; const a = my - b * mx;
              const wr = validScInd.map((p) => ({ ...p, regression: a + b * p.revenue }));
              setRevenueExpensesScatterData(sortChartRowsByDate(wr));
            } else {
              setRevenueExpensesScatterData(
                sortChartRowsByDate(validScInd.map((p) => ({ ...p, regression: p.expenses })))
              );
            }
            setCorrelationRc(data.analytics?.pl?.correlation_rc ?? null);

            const pmPtsInd: { date: string; profit: number; mps: number }[] = [];
            chronoDatesInd.forEach((date) => {
              const qe = getEntry(date)?.pl_as_qe as { "PROFIT/(LOSS) FOR THE PERIOD"?: number; mps?: number } | undefined;
              if (!qe) return;
              pmPtsInd.push({ date, profit: typeof qe["PROFIT/(LOSS) FOR THE PERIOD"] === "number" ? qe["PROFIT/(LOSS) FOR THE PERIOD"] : 0, mps: typeof qe.mps === "number" ? qe.mps : 0 });
            });
            const validPmInd = pmPtsInd.filter((p) => p.profit !== 0 || p.mps !== 0);
            if (validPmInd.length >= 2) {
              const xs = validPmInd.map((p) => p.profit); const ys = validPmInd.map((p) => p.mps); const n = xs.length;
              const mx = xs.reduce((a, b) => a + b, 0) / n; const my = ys.reduce((a, b) => a + b, 0) / n;
              const cov = xs.reduce((acc, x, i) => acc + (x - mx) * (ys[i] - my), 0) / n;
              const vx = xs.reduce((acc, x) => acc + (x - mx) ** 2, 0) / n;
              const b = vx !== 0 ? cov / vx : 0; const a = my - b * mx;
              const wr = validPmInd.map((p) => ({ ...p, regression: a + b * p.profit }));
              setProfitMpsScatterData(sortChartRowsByDate(wr));
            } else {
              setProfitMpsScatterData(sortChartRowsByDate(validPmInd.map((p) => ({ ...p, regression: p.mps }))));
            }
            setCorrelationProfitMp(data.analytics?.pl?.["correlation_profit-mp"] ?? null);

            const rmPtsInd: { date: string; revenue: number; mps: number }[] = [];
            chronoDatesInd.forEach((date) => {
              const qe = getEntry(date)?.pl_as_qe as { "TOTAL REVENUE"?: number; mps?: number } | undefined;
              if (!qe) return;
              rmPtsInd.push({ date, revenue: typeof qe["TOTAL REVENUE"] === "number" ? qe["TOTAL REVENUE"] : 0, mps: typeof qe.mps === "number" ? qe.mps : 0 });
            });
            const validRmInd = rmPtsInd.filter((p) => p.revenue !== 0 || p.mps !== 0);
            if (validRmInd.length >= 2) {
              const xs = validRmInd.map((p) => p.revenue); const ys = validRmInd.map((p) => p.mps); const n = xs.length;
              const mx = xs.reduce((a, b) => a + b, 0) / n; const my = ys.reduce((a, b) => a + b, 0) / n;
              const cov = xs.reduce((acc, x, i) => acc + (x - mx) * (ys[i] - my), 0) / n;
              const vx = xs.reduce((acc, x) => acc + (x - mx) ** 2, 0) / n;
              const b = vx !== 0 ? cov / vx : 0; const a = my - b * mx;
              const wr = validRmInd.map((p) => ({ ...p, regression: a + b * p.revenue }));
              setRevenueMpsScatterData(sortChartRowsByDate(wr));
            } else {
              setRevenueMpsScatterData(sortChartRowsByDate(validRmInd.map((p) => ({ ...p, regression: p.mps }))));
            }
            setCorrelationRevenueMp(data.analytics?.pl?.["correlation_revenue-mp"] ?? null);

            setPlData(items);
            setDataLoaded(true);
            console.log("Ind AS P&L data loaded for dates:", allDates);
            return;
          }

          // ════════════════════════════════════════════════════════════════
          // AS path (existing logic)
          // ════════════════════════════════════════════════════════════════
          const onlyMarch31 = hasOnlyMarch31Dates(allDates);
          // Column order: always latest date leftmost (newest→oldest)
          const displayDates = allDates;
          setDates(displayDates);
          setShowOnlyYe(onlyMarch31);

          // Amount columns: only 31/03 = one YE column per date; mixed = QE for every date + YE only for 31/03 and latest
          const columns: DateColumn[] = onlyMarch31
            ? displayDates.map((d) => ({ type: "YE", date: d }))
            : (() => {
                const list: DateColumn[] = [];
                const latest = allDates[0];
                for (const date of allDates) {
                  if (date === latest || /^31\/03\/(\d{2}|\d{4})$/.test(date)) list.push({ type: "YE", date });
                  list.push({ type: "QE", date });
                }
                return list;
              })();
          setDateColumns(columns);

          // Narrow date-key access (data may also have analytics key)
          const getEntry = (d: string) => getDateEntry(data, d);
          // YE value (pl_as_ye or legacy pl_as) – return 0 when missing so display is always from JSON or fallback
          const getValueYe = (key: string, date: string): number => {
            const byYe = getEntry(date)?.pl_as_ye?.[key];
            if (typeof byYe === "number") return byYe;
            const legacy = getEntry(date)?.pl_as?.[key];
            if (typeof legacy === "number") return legacy;
            return 0;
          };
          // Optional: get from JSON only (undefined if not present)
          const getValueYeFromJson = (key: string, date: string): number | undefined => {
            const byYe = getEntry(date)?.pl_as_ye?.[key];
            if (typeof byYe === "number") return byYe;
            const legacy = getEntry(date)?.pl_as?.[key];
            if (typeof legacy === "number") return legacy;
            return undefined;
          };
          const getValueQeFromJson = (key: string, date: string): number | undefined => {
            const qe = getEntry(date)?.pl_as_qe?.[key];
            return typeof qe === "number" ? qe : undefined;
          };
          // QE value (pl_as_qe) – only used when there are non–31/03 dates
          const getValueQe = (key: string, date: string): number => {
            const qe = getEntry(date)?.pl_as_qe?.[key];
            return typeof qe === "number" ? qe : 0;
          };
          // When only 31/03 we show only YE
          const getValue = (key: string, date: string): number => getValueYe(key, date);

          // Build P&L items according to the specified format
          const items: PLItem[] = [];

          // I. Revenue from Operations
          const revenueFromOpsValues: { [date: string]: number } = {};
          const revenueFromOpsValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            revenueFromOpsValues[date] = getValue("Revenue from Operations", date);
            if (!onlyMarch31) revenueFromOpsValuesQe[date] = getValueQe("Revenue from Operations", date);
          });
          items.push({
            label: "I. Revenue from Operations",
            key: "Revenue from Operations",
            values: revenueFromOpsValues,
            valuesQe: onlyMarch31 ? undefined : revenueFromOpsValuesQe,
            level: 0,
            isExpense: false,
          });

          // II. Other Income
          const otherIncomeValues: { [date: string]: number } = {};
          const otherIncomeValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            otherIncomeValues[date] = getValue("Other Income", date);
            if (!onlyMarch31) otherIncomeValuesQe[date] = getValueQe("Other Income", date);
          });
          items.push({
            label: "II. Other Income",
            key: "Other Income",
            values: otherIncomeValues,
            valuesQe: onlyMarch31 ? undefined : otherIncomeValuesQe,
            level: 0,
            isExpense: false,
          });

          // III. TOTAL REVENUE (from JSON when present, else sum)
          const totalRevenueValues: { [date: string]: number } = {};
          const totalRevenueValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            totalRevenueValues[date] = getValueYeFromJson("TOTAL REVENUE", date) ?? (revenueFromOpsValues[date] + otherIncomeValues[date]);
            if (!onlyMarch31) totalRevenueValuesQe[date] = getValueQeFromJson("TOTAL REVENUE", date) ?? (revenueFromOpsValuesQe[date] + otherIncomeValuesQe[date]);
          });
          items.push({
            label: "III. TOTAL REVENUE",
            values: totalRevenueValues,
            valuesQe: onlyMarch31 ? undefined : totalRevenueValuesQe,
            level: 0,
            isTotal: true,
            isExpense: false,
          });

          // IV. EXPENSES (section header)
          items.push({
            label: "IV. EXPENSES",
            values: {},
            level: 0,
            isSectionHeader: true,
            isExpense: true,
          });

          // Expense items
          const costOfMaterialsValues: { [date: string]: number } = {};
          const costOfMaterialsValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            costOfMaterialsValues[date] = getValue("Cost of materials consumed", date);
            if (!onlyMarch31) costOfMaterialsValuesQe[date] = getValueQe("Cost of materials consumed", date);
          });
          items.push({
            label: "Cost of materials consumed",
            key: "Cost of materials consumed",
            values: costOfMaterialsValues,
            valuesQe: onlyMarch31 ? undefined : costOfMaterialsValuesQe,
            level: 1,
            isSubItem: true,
            isExpense: true,
          });

          const purchasesValues: { [date: string]: number } = {};
          const purchasesValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            purchasesValues[date] = getValue("Purchases of stock-in-trade", date);
            if (!onlyMarch31) purchasesValuesQe[date] = getValueQe("Purchases of stock-in-trade", date);
          });
          items.push({
            label: "Purchases of stock-in-trade",
            key: "Purchases of stock-in-trade",
            values: purchasesValues,
            valuesQe: onlyMarch31 ? undefined : purchasesValuesQe,
            level: 1,
            isSubItem: true,
            isExpense: true,
          });

          const inventoryChangesValues: { [date: string]: number } = {};
          const inventoryChangesValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            const val =
              getValue("Changes in inventories", date) ||
              getValue("Changes in inventories of finished goods, work-in-progress and stock-in-trade", date);
            inventoryChangesValues[date] = val;
            if (!onlyMarch31) {
              const qe =
                getValueQe("Changes in inventories", date) ||
                getValueQe("Changes in inventories of finished goods, work-in-progress and stock-in-trade", date);
              inventoryChangesValuesQe[date] = qe;
            }
          });
          items.push({
            label: "Changes in inventories",
            key: "Changes in inventories",
            values: inventoryChangesValues,
            valuesQe: onlyMarch31 ? undefined : inventoryChangesValuesQe,
            level: 1,
            isSubItem: true,
            isExpense: true,
          });

          const employeeBenefitsValues: { [date: string]: number } = {};
          const employeeBenefitsValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            employeeBenefitsValues[date] = getValue("Employee benefits expense", date);
            if (!onlyMarch31) employeeBenefitsValuesQe[date] = getValueQe("Employee benefits expense", date);
          });
          items.push({
            label: "Employee benefits expense",
            key: "Employee benefits expense",
            values: employeeBenefitsValues,
            valuesQe: onlyMarch31 ? undefined : employeeBenefitsValuesQe,
            level: 1,
            isSubItem: true,
            isExpense: true,
          });

          const financeCostsValues: { [date: string]: number } = {};
          const financeCostsValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            financeCostsValues[date] = getValue("Finance costs", date);
            if (!onlyMarch31) financeCostsValuesQe[date] = getValueQe("Finance costs", date);
          });
          items.push({
            label: "Finance costs",
            key: "Finance costs",
            values: financeCostsValues,
            valuesQe: onlyMarch31 ? undefined : financeCostsValuesQe,
            level: 1,
            isSubItem: true,
            isExpense: true,
          });

          const depreciationValues: { [date: string]: number } = {};
          const depreciationValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            depreciationValues[date] = getValue("Depreciation and amortisation expense", date);
            if (!onlyMarch31) depreciationValuesQe[date] = getValueQe("Depreciation and amortisation expense", date);
          });
          items.push({
            label: "Depreciation and amortisation expense",
            key: "Depreciation and amortisation expense",
            values: depreciationValues,
            valuesQe: onlyMarch31 ? undefined : depreciationValuesQe,
            level: 1,
            isSubItem: true,
            isExpense: true,
          });

          const otherExpensesValues: { [date: string]: number } = {};
          const otherExpensesValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            otherExpensesValues[date] = getValue("Other Expenses", date);
            if (!onlyMarch31) otherExpensesValuesQe[date] = getValueQe("Other Expenses", date);
          });
          items.push({
            label: "Other Expenses",
            key: "Other Expenses",
            values: otherExpensesValues,
            valuesQe: onlyMarch31 ? undefined : otherExpensesValuesQe,
            level: 1,
            isSubItem: true,
            isExpense: true,
          });

          // TOTAL EXPENSES (from JSON when present, else sum)
          const totalExpensesValues: { [date: string]: number } = {};
          const totalExpensesValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            const sumYe = costOfMaterialsValues[date] + purchasesValues[date] + inventoryChangesValues[date] +
              employeeBenefitsValues[date] + financeCostsValues[date] + depreciationValues[date] + otherExpensesValues[date];
            totalExpensesValues[date] = getValueYeFromJson("TOTAL EXPENSES", date) ?? sumYe;
            if (!onlyMarch31) {
              const sumQe = costOfMaterialsValuesQe[date] + purchasesValuesQe[date] + inventoryChangesValuesQe[date] +
                employeeBenefitsValuesQe[date] + financeCostsValuesQe[date] + depreciationValuesQe[date] + otherExpensesValuesQe[date];
              totalExpensesValuesQe[date] = getValueQeFromJson("TOTAL EXPENSES", date) ?? sumQe;
            }
          });
          items.push({
            label: "TOTAL EXPENSES",
            values: totalExpensesValues,
            valuesQe: onlyMarch31 ? undefined : totalExpensesValuesQe,
            level: 0,
            isTotal: true,
            isExpense: true,
          });

          // V. PROFIT BEFORE EXCEPTIONAL ITEMS, EXTRAORDINARY ITEMS AND TAX (from JSON when present, else III-IV)
          const profitBeforeExceptionalValues: { [date: string]: number } = {};
          const profitBeforeExceptionalValuesQe: { [date: string]: number } = {};
          const PROFIT_BEFORE_EXCEPTIONAL_KEY = "PROFIT BEFORE EXCEPTIONAL ITEMS, EXTRAORDINARY ITEMS AND TAX";
          allDates.forEach(date => {
            profitBeforeExceptionalValues[date] = getValueYeFromJson(PROFIT_BEFORE_EXCEPTIONAL_KEY, date) ?? (totalRevenueValues[date] - totalExpensesValues[date]);
            if (!onlyMarch31) profitBeforeExceptionalValuesQe[date] = getValueQeFromJson(PROFIT_BEFORE_EXCEPTIONAL_KEY, date) ?? (totalRevenueValuesQe[date] - totalExpensesValuesQe[date]);
          });
          items.push({
            label: "V. PROFIT BEFORE EXCEPTIONAL ITEMS, EXTRAORDINARY ITEMS AND TAX (III-IV)",
            values: profitBeforeExceptionalValues,
            valuesQe: onlyMarch31 ? undefined : profitBeforeExceptionalValuesQe,
            level: 0,
            isTotal: true,
            isExpense: false,
          });

          // VI. EXCEPTIONAL ITEMS (section header)
          items.push({
            label: "VI. EXCEPTIONAL ITEMS",
            values: {},
            level: 0,
            isSectionHeader: true,
            isExpense: true,
          });

          const exceptionalItemsValues: { [date: string]: number } = {};
          const exceptionalItemsValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            exceptionalItemsValues[date] = getValue("Exceptional Items", date);
            if (!onlyMarch31) exceptionalItemsValuesQe[date] = getValueQe("Exceptional Items", date);
          });

          const profitBeforeExtraordinaryValues: { [date: string]: number } = {};
          const profitBeforeExtraordinaryValuesQe: { [date: string]: number } = {};
          const PROFIT_BEFORE_EXTRAORDINARY_KEY = "PROFIT BEFORE EXTRAORDINARY ITEMS AND TAX";
          allDates.forEach(date => {
            profitBeforeExtraordinaryValues[date] = getValueYeFromJson(PROFIT_BEFORE_EXTRAORDINARY_KEY, date) ?? (profitBeforeExceptionalValues[date] - exceptionalItemsValues[date]);
            if (!onlyMarch31) profitBeforeExtraordinaryValuesQe[date] = getValueQeFromJson(PROFIT_BEFORE_EXTRAORDINARY_KEY, date) ?? (profitBeforeExceptionalValuesQe[date] - exceptionalItemsValuesQe[date]);
          });
          items.push({
            label: "VII. PROFIT BEFORE EXTRAORDINARY ITEMS AND TAX (V-VI)",
            values: profitBeforeExtraordinaryValues,
            valuesQe: onlyMarch31 ? undefined : profitBeforeExtraordinaryValuesQe,
            level: 0,
            isTotal: true,
            isExpense: false,
          });

          items.push({
            label: "VIII. EXTRAORDINARY ITEMS",
            values: {},
            level: 0,
            isSectionHeader: true,
            isExpense: true,
          });

          const extraordinaryItemsValues: { [date: string]: number } = {};
          const extraordinaryItemsValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            extraordinaryItemsValues[date] = getValue("Extraordinary Items", date);
            if (!onlyMarch31) extraordinaryItemsValuesQe[date] = getValueQe("Extraordinary Items", date);
          });

          const profitBeforeTaxValues: { [date: string]: number } = {};
          const profitBeforeTaxValuesQe: { [date: string]: number } = {};
          const PROFIT_BEFORE_TAX_KEY = "PROFIT BEFORE TAX";
          allDates.forEach(date => {
            profitBeforeTaxValues[date] = getValueYeFromJson(PROFIT_BEFORE_TAX_KEY, date) ?? (profitBeforeExtraordinaryValues[date] - extraordinaryItemsValues[date]);
            if (!onlyMarch31) profitBeforeTaxValuesQe[date] = getValueQeFromJson(PROFIT_BEFORE_TAX_KEY, date) ?? (profitBeforeExtraordinaryValuesQe[date] - extraordinaryItemsValuesQe[date]);
          });
          items.push({
            label: "IX. PROFIT BEFORE TAX (VII-VIII)",
            values: profitBeforeTaxValues,
            valuesQe: onlyMarch31 ? undefined : profitBeforeTaxValuesQe,
            level: 0,
            isTotal: true,
            isExpense: false,
          });

          items.push({
            label: "X. TAX EXPENSE",
            values: {},
            level: 0,
            isSectionHeader: true,
            isExpense: true,
          });

          const currentTaxValues: { [date: string]: number } = {};
          const currentTaxValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            currentTaxValues[date] = getValue("Current tax", date);
            if (!onlyMarch31) currentTaxValuesQe[date] = getValueQe("Current tax", date);
          });
          items.push({
            label: "1. Current tax",
            key: "Current tax",
            values: currentTaxValues,
            valuesQe: onlyMarch31 ? undefined : currentTaxValuesQe,
            level: 1,
            isSubItem: true,
            isExpense: true,
          });

          const deferredTaxValues: { [date: string]: number } = {};
          const deferredTaxValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            deferredTaxValues[date] = getValue("Deferred tax", date);
            if (!onlyMarch31) deferredTaxValuesQe[date] = getValueQe("Deferred tax", date);
          });
          items.push({
            label: "2. Deferred tax",
            key: "Deferred tax",
            values: deferredTaxValues,
            valuesQe: onlyMarch31 ? undefined : deferredTaxValuesQe,
            level: 1,
            isSubItem: true,
            isExpense: true,
          });

          const totalTaxValues: { [date: string]: number } = {};
          const totalTaxValuesQe: { [date: string]: number } = {};
          allDates.forEach(date => {
            const sumTaxYe = currentTaxValues[date] + deferredTaxValues[date];
            totalTaxValues[date] = getValueYeFromJson("Total tax", date) ?? getValueYeFromJson("Total Tax", date) ?? sumTaxYe;
            if (!onlyMarch31) {
              const sumTaxQe = currentTaxValuesQe[date] + deferredTaxValuesQe[date];
              totalTaxValuesQe[date] = getValueQeFromJson("Total tax", date) ?? getValueQeFromJson("Total Tax", date) ?? sumTaxQe;
            }
          });
          items.push({
            label: "",
            values: totalTaxValues,
            valuesQe: onlyMarch31 ? undefined : totalTaxValuesQe,
            level: 0,
            isTotal: true,
            isSubItem: false,
            isExpense: true,
          });

          const profitLossValues: { [date: string]: number } = {};
          const profitLossValuesQe: { [date: string]: number } = {};
          const PROFIT_LOSS_PERIOD_KEY = "PROFIT/(LOSS) FOR THE PERIOD";
          allDates.forEach(date => {
            profitLossValues[date] = getValueYeFromJson(PROFIT_LOSS_PERIOD_KEY, date) ?? (profitBeforeTaxValues[date] - totalTaxValues[date]);
            if (!onlyMarch31) profitLossValuesQe[date] = getValueQeFromJson(PROFIT_LOSS_PERIOD_KEY, date) ?? (profitBeforeTaxValuesQe[date] - totalTaxValuesQe[date]);
          });
          items.push({
            label: "XI. PROFIT/(LOSS) FOR THE PERIOD (IX-X)",
            values: profitLossValues,
            valuesQe: onlyMarch31 ? undefined : profitLossValuesQe,
            level: 0,
            isTotal: true,
            isExpense: false,
          });

          // Enrich items with CAGR (from FinData_Formulae.json analytics) and Trend (computed from values)
          const plCagr = data.analytics?.pl?.cagr;
          const plTrend = data.analytics?.pl?.trend;
          const getCagrKey = (item: PLItem): string | undefined => {
            if (item.key) return item.key;
            if (item.label.includes("TOTAL REVENUE") && !item.label.includes("Revenue from Operations")) return "TOTAL REVENUE";
            if (item.label.includes("TOTAL EXPENSES")) return "TOTAL EXPENSES";
            if (item.label.includes("PROFIT/(LOSS) FOR THE PERIOD")) return "PROFIT/(LOSS) FOR THE PERIOD";
            if (item.label.includes("PROFIT BEFORE TAX")) return "PROFIT BEFORE TAX";
            if (item.label.includes("PROFIT BEFORE EXCEPTIONAL")) return "PROFIT BEFORE EXCEPTIONAL ITEMS, EXTRAORDINARY ITEMS AND TAX";
            if (item.label.includes("PROFIT BEFORE EXTRAORDINARY")) return "PROFIT BEFORE EXTRAORDINARY ITEMS AND TAX";
            return undefined;
          };
          // Trend: use QE figures if any QE columns are displayed; otherwise use YE figures only.
          // Chronological order (oldest→newest) for trend computation and sparkline – unaffected by column display order.
          const hasQeColumns = columns.some((c) => c.type === "QE");
          const chronologicalOrder = sortDatesOldestFirst(allDates);
          items.forEach((item) => {
            const cagrKey = getCagrKey(item);
            item.cagr = cagrKey && plCagr && plCagr[cagrKey] != null ? plCagr[cagrKey]! : null;
            if (plTrend && cagrKey && plTrend[cagrKey] != null) {
              item.trend = plTrend[cagrKey]!;
            } else if (!item.isSectionHeader && chronologicalOrder.length >= 2) {
              const trendValues = hasQeColumns ? item.valuesQe : item.values;
              if (trendValues && Object.keys(trendValues).length > 0) {
                const oldest = chronologicalOrder[0];
                const newest = chronologicalOrder[chronologicalOrder.length - 1];
                const firstVal = trendValues[oldest];
                const lastVal = trendValues[newest];
                if (typeof firstVal === "number" && typeof lastVal === "number") {
                  if (lastVal > firstVal) item.trend = "Up";
                  else if (lastVal < firstVal) item.trend = "Down";
                  else item.trend = "Stable";
                }
              }
            }
          });

          const initialNoOfShares: Record<string, string> = {};
          const initialEps: Record<string, string> = {};
          const initialMps: Record<string, string> = {};
          allDates.forEach((date) => {
            const ye = getEntry(date)?.pl_as_ye as { no_of_shares?: number; eps?: number; mps?: number } | undefined;
            if (ye) {
              const k = `${date}|YE`;
              if (ye.no_of_shares != null) initialNoOfShares[k] = String(ye.no_of_shares);
              if (ye.eps != null && ye.eps !== 0) initialEps[k] = String(ye.eps);
              if (ye.mps != null) initialMps[k] = String(ye.mps);
            }
            const qe = getEntry(date)?.pl_as_qe as { no_of_shares?: number; eps?: number; mps?: number } | undefined;
            if (qe) {
              const k = `${date}|QE`;
              if (qe.no_of_shares != null) initialNoOfShares[k] = String(qe.no_of_shares);
              if (qe.eps != null && qe.eps !== 0) initialEps[k] = String(qe.eps);
              if (qe.mps != null) initialMps[k] = String(qe.mps);
            }
          });
          setNoOfShares((prev) => ({ ...prev, ...initialNoOfShares }));
          setEps((prev) => ({ ...prev, ...initialEps }));
          setMps((prev) => ({ ...prev, ...initialMps }));

          const chronologicalDates = sortDatesOldestFirst(allDates);
          const npRatioChart = sortChartRowsByDate(
            chronologicalDates.map((date) => ({
              date,
              sortKey: parseDate(date).getTime(),
              value: getEntry(date)?.formulae?.Net_profit_ratio ?? 0,
            }))
          );
          setNetProfitRatioChartData(npRatioChart);

          // Revenue vs Total Expenses scatter (pl_as_qe): Revenue = independent (X), Total Expenses = dependent (Y). Linear regression line.
          const scatterPoints: { date: string; revenue: number; expenses: number }[] = [];
          chronologicalDates.forEach((date) => {
            const qe = getEntry(date)?.pl_as_qe;
            if (!qe) return;
            const revenue = typeof qe["TOTAL REVENUE"] === "number" ? qe["TOTAL REVENUE"] : 0;
            const expenses = typeof qe["TOTAL EXPENSES"] === "number" ? qe["TOTAL EXPENSES"] : 0;
            scatterPoints.push({ date, revenue, expenses });
          });
          const validScatter = scatterPoints.filter((p) => typeof p.revenue === "number" && typeof p.expenses === "number" && (p.revenue !== 0 || p.expenses !== 0));
          if (validScatter.length >= 2) {
            const xs = validScatter.map((p) => p.revenue);
            const ys = validScatter.map((p) => p.expenses);
            const n = xs.length;
            const meanX = xs.reduce((a, b) => a + b, 0) / n;
            const meanY = ys.reduce((a, b) => a + b, 0) / n;
            const cov = xs.reduce((acc, x, i) => acc + (x - meanX) * (ys[i] - meanY), 0) / n;
            const varX = xs.reduce((acc, x) => acc + (x - meanX) ** 2, 0) / n;
            const b = varX !== 0 ? cov / varX : 0;
            const a = meanY - b * meanX;
            const withRegression = validScatter.map((p) => ({ ...p, regression: a + b * p.revenue }));
            setRevenueExpensesScatterData(sortChartRowsByDate(withRegression));
          } else {
            setRevenueExpensesScatterData(
              sortChartRowsByDate(validScatter.map((p) => ({ ...p, regression: p.expenses })))
            );
          }
          setCorrelationRc(data.analytics?.pl?.correlation_rc ?? null);

          // Profit/(loss) vs Market price per share scatter (pl_as_qe): X = profit, Y = mps. Linear regression.
          const profitMpsPoints: { date: string; profit: number; mps: number }[] = [];
          chronologicalDates.forEach((date) => {
            const qe = getEntry(date)?.pl_as_qe as { "PROFIT/(LOSS) FOR THE PERIOD"?: number; mps?: number } | undefined;
            if (!qe) return;
            const profit = typeof qe["PROFIT/(LOSS) FOR THE PERIOD"] === "number" ? qe["PROFIT/(LOSS) FOR THE PERIOD"] : 0;
            const mps = typeof qe.mps === "number" ? qe.mps : 0;
            profitMpsPoints.push({ date, profit, mps });
          });
          const validProfitMps = profitMpsPoints.filter((p) => (p.profit !== 0 || p.mps !== 0) && (typeof p.mps === "number" && typeof p.profit === "number"));
          if (validProfitMps.length >= 2) {
            const xs = validProfitMps.map((p) => p.profit);
            const ys = validProfitMps.map((p) => p.mps);
            const n = xs.length;
            const meanX = xs.reduce((a, b) => a + b, 0) / n;
            const meanY = ys.reduce((a, b) => a + b, 0) / n;
            const cov = xs.reduce((acc, x, i) => acc + (x - meanX) * (ys[i] - meanY), 0) / n;
            const varX = xs.reduce((acc, x) => acc + (x - meanX) ** 2, 0) / n;
            const b = varX !== 0 ? cov / varX : 0;
            const a = meanY - b * meanX;
            const withReg = validProfitMps.map((p) => ({ ...p, regression: a + b * p.profit }));
            setProfitMpsScatterData(sortChartRowsByDate(withReg));
          } else {
            setProfitMpsScatterData(sortChartRowsByDate(validProfitMps.map((p) => ({ ...p, regression: p.mps }))));
          }
          setCorrelationProfitMp(data.analytics?.pl?.["correlation_profit-mp"] ?? null);

          // Revenue vs Market price per share scatter (pl_as_qe): X = revenue, Y = mps. Linear regression.
          const revenueMpsPoints: { date: string; revenue: number; mps: number }[] = [];
          chronologicalDates.forEach((date) => {
            const qe = getEntry(date)?.pl_as_qe as { "TOTAL REVENUE"?: number; mps?: number } | undefined;
            if (!qe) return;
            const revenue = typeof qe["TOTAL REVENUE"] === "number" ? qe["TOTAL REVENUE"] : 0;
            const mps = typeof qe.mps === "number" ? qe.mps : 0;
            revenueMpsPoints.push({ date, revenue, mps });
          });
          const validRevenueMps = revenueMpsPoints.filter((p) => (p.revenue !== 0 || p.mps !== 0) && (typeof p.mps === "number" && typeof p.revenue === "number"));
          if (validRevenueMps.length >= 2) {
            const xs = validRevenueMps.map((p) => p.revenue);
            const ys = validRevenueMps.map((p) => p.mps);
            const n = xs.length;
            const meanX = xs.reduce((a, b) => a + b, 0) / n;
            const meanY = ys.reduce((a, b) => a + b, 0) / n;
            const cov = xs.reduce((acc, x, i) => acc + (x - meanX) * (ys[i] - meanY), 0) / n;
            const varX = xs.reduce((acc, x) => acc + (x - meanX) ** 2, 0) / n;
            const b = varX !== 0 ? cov / varX : 0;
            const a = meanY - b * meanX;
            const withReg = validRevenueMps.map((p) => ({ ...p, regression: a + b * p.revenue }));
            setRevenueMpsScatterData(sortChartRowsByDate(withReg));
          } else {
            setRevenueMpsScatterData(sortChartRowsByDate(validRevenueMps.map((p) => ({ ...p, regression: p.mps }))));
          }
          setCorrelationRevenueMp(data.analytics?.pl?.["correlation_revenue-mp"] ?? null);

          setPlData(items);
          setDataLoaded(true);
          console.log('Profit & Loss data loaded successfully for dates:', allDates);
        } else {
          throw new Error(result?.error || "Failed to download financial data");
        }
      }
    } catch (err) {
      console.error('Failed to load Profit & Loss data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Auto-load data when component mounts
  useEffect(() => {
    loadFinancialData();
  }, []);


  // Show load button if data hasn't been loaded yet
  if (!dataLoaded && !loading) {
    return (
      <div className="flex h-full items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold text-white mb-6">Profit & Loss Statement</h2>
          <p className="text-white/80 text-lg">Close Trial Balance for these pages to load</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-white" />
          <span className="text-white">Loading Profit & Loss data</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-400 mb-2">Error loading data</div>
          <div className="text-sm text-white/60 mb-4">{error}</div>
          <Button 
            onClick={loadFinancialData}
            className="mt-4 bg-[#4da3ff] hover:bg-[#3a8bcc] text-white"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // ── Ind AS P&L renderer ─────────────────────────────────────────────────────
  const renderIndASPLTable = () => {
    const [d1, d2] = indAsDates;
    // Opaque background for sticky column so scrolling columns don't show through
    const stickyCellBg = "bg-[var(--card)]";
    const stickyCellBgTotal = "bg-[var(--card)]"; // keep opaque for totals too
    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead
                className={`sticky left-0 z-20 w-[200px] max-w-[200px] min-w-[200px] ${stickyCellBg} border-r border-white/10 font-medium text-white/60 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.2)]`}
              >
                Particulars
              </TableHead>
              {d1 && (
                <TableHead className="font-semibold text-white text-right min-w-[120px] max-w-[160px] whitespace-normal break-words border-white/10">
                  For the year ended {d1}
                </TableHead>
              )}
              {d2 && (
                <TableHead className="font-semibold text-white text-right min-w-[120px] max-w-[160px] whitespace-normal break-words border-white/10">
                  For the year ended {d2}
                </TableHead>
              )}
              <TableHead className="font-semibold text-white text-right min-w-[100px] whitespace-nowrap border-white/10">
                Variance
              </TableHead>
              <TableHead className="font-semibold text-white text-right min-w-[90px] whitespace-nowrap border-white/10">
                Variance %
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {indAsPlRows.map((row, idx) => {
              const v1 = d1 ? (row.values[d1] ?? 0) : 0;
              const v2 = d2 ? (row.values[d2] ?? 0) : 0;
              const variance = v1 - v2;
              const isBlank = row.type === "blank";
              const isGrandTotal = row.type === "grandTotal";
              const isSubTotal = row.type === "subTotal";
              const isSectionHeader = row.type === "sectionHeader";
              const hasValue = row.type === "lineItem" || isSubTotal || isGrandTotal;

              if (isBlank) {
                return (
                  <TableRow key={idx} className="border-0 hover:bg-transparent h-3">
                    <TableCell className={`sticky left-0 z-20 w-[200px] max-w-[200px] min-w-[200px] p-0 ${stickyCellBg} border-r border-white/10`} />
                    <TableCell className="p-0 border-white/10" />
                    <TableCell className="p-0 border-white/10" />
                    <TableCell className="p-0 border-white/10" />
                    <TableCell className="p-0 border-white/10" />
                  </TableRow>
                );
              }

              const rowBorder = isGrandTotal
                ? "border-t-2 border-white/30"
                : isSubTotal
                  ? "border-t border-white/20"
                  : "border-white/10";

              const nameStyle: React.CSSProperties = {
                paddingLeft: `${8 + row.level * 20}px`,
              };

              const nameClass = isGrandTotal
                ? "font-bold text-white text-sm uppercase tracking-wide break-words"
                : isSectionHeader
                  ? "font-bold text-white text-sm break-words"
                  : isSubTotal
                    ? "text-white/0 select-none"
                    : "text-white/90 text-sm break-words";

              const valueCellClass = `font-mono text-right text-sm whitespace-nowrap ${
                isGrandTotal ? "font-bold text-white" : isSubTotal ? "font-semibold text-white" : "text-white/90"
              }`;

              const varianceColor =
                !hasValue || variance === 0
                  ? "text-white/60"
                  : variance > 0
                    ? "text-emerald-400"
                    : "text-red-400";

              const stickyBg = isGrandTotal || isSubTotal ? stickyCellBgTotal : stickyCellBg;

              return (
                <TableRow key={idx} className={`hover:bg-white/5 ${rowBorder}`}>
                  <TableCell
                    className={`sticky left-0 z-20 w-[200px] max-w-[200px] min-w-[200px] ${stickyBg} border-r border-white/10 py-1.5 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.2)] align-top whitespace-normal`}
                    style={nameStyle}
                  >
                    <span className={`${nameClass} block min-w-0 whitespace-normal break-words`}>{row.name}</span>
                  </TableCell>
                  <TableCell className={`${valueCellClass} py-1.5 border-white/10`}>
                    {hasValue ? (v1 === 0 && !isSubTotal && !isGrandTotal ? "—" : formatCrores(v1)) : ""}
                  </TableCell>
                  <TableCell className={`${valueCellClass} py-1.5 border-white/10`}>
                    {hasValue && d2 ? (v2 === 0 && !isSubTotal && !isGrandTotal ? "—" : formatCrores(v2)) : ""}
                  </TableCell>
                  <TableCell className={`font-mono text-right text-sm py-1.5 whitespace-nowrap border-white/10 ${hasValue && d2 ? varianceColor : ""}`}>
                    {hasValue && d2
                      ? variance === 0 ? "—" : `${variance > 0 ? "+" : ""}${formatCrores(variance)}`
                      : ""}
                  </TableCell>
                  <TableCell className={`font-mono text-right text-sm py-1.5 whitespace-nowrap border-white/10 ${hasValue && d2 ? varianceColor : ""}`}>
                    {hasValue && d2 && (v1 !== 0 || v2 !== 0) ? formatVariancePct(v1, v2) : ""}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  // ── Shared UI (AS and Ind AS) ─────────────────────────────────────────────
  return (
    <div className="space-y-6 text-white">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Profit & Loss Statement</h2>
          <p className="text-white/60">{companyName}</p>
          {frf === "Indian Accounting Standards (Ind AS)" && (
            <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
              Indian Accounting Standards (Ind AS)
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {analyticsMessage && (
            <span className="text-sm text-emerald-300">
              {analyticsMessage}
            </span>
          )}
          <Button
            size="sm"
            className="bg-[#4da3ff] hover:bg-[#3a8bcc] text-white"
            disabled={analyticsLoading}
            onClick={async () => {
              const api = (window as any).sharePointAPI;
              if (!api?.downloadClientJson || !api?.uploadClientJson || !api?.generateFinancialAnalytics) {
                setAnalyticsMessage("SharePoint API not available");
                return;
              }
              try {
                setAnalyticsLoading(true);
                setAnalyticsMessage(null);
                const downloadResult = await api.downloadClientJson("FinData_Formulae.json");
                if (!downloadResult?.success || !downloadResult.data) {
                  setAnalyticsMessage(downloadResult?.error || "Failed to download financial data");
                  return;
                }
                const data = { ...downloadResult.data } as ProfitLossData;
                const colKey = (col: DateColumn) => `${col.date}|${col.type}`;
                const profitLossItem = plData.find((i) => i.label.includes("PROFIT/(LOSS) FOR THE PERIOD"));
                dateColumns.forEach((col) => {
                  if (!getDateEntry(data, col.date)) (data as Record<string, ProfitLossDateEntry>)[col.date] = {} as ProfitLossDateEntry;
                  const entry = (data as Record<string, ProfitLossDateEntry>)[col.date]!;
                  const plYe = (entry.pl_as_ye ??= {} as Record<string, number>);
                  const plQe = (entry.pl_as_qe ??= {} as Record<string, number>);
                  const k = colKey(col);
                  const n = noOfShares[k];
                  const e = eps[k];
                  const m = mps[k];
                  const updates: { no_of_shares?: number; eps?: number; mps?: number } = {};
                  if (n !== undefined && n !== "") {
                    const num = parseFloat(n);
                    if (!Number.isNaN(num)) updates.no_of_shares = num;
                  }
                  if (e !== undefined && e !== "") {
                    const num = parseFloat(e);
                    if (!Number.isNaN(num)) updates.eps = num;
                  } else if (n !== undefined && n !== "") {
                    const sharesNum = parseFloat(n);
                    if (!Number.isNaN(sharesNum) && sharesNum !== 0 && profitLossItem) {
                      const profitVal = col.type === "QE" && profitLossItem.valuesQe ? profitLossItem.valuesQe[col.date] : profitLossItem.values[col.date];
                      if (typeof profitVal === "number") updates.eps = profitVal / sharesNum;
                    }
                  }
                  if (m !== undefined && m !== "") {
                    const num = parseFloat(m);
                    if (!Number.isNaN(num)) updates.mps = num;
                  }
                  [plYe, plQe].forEach((target) => {
                    if (updates.no_of_shares !== undefined) target.no_of_shares = updates.no_of_shares;
                    if (updates.eps !== undefined) target.eps = updates.eps;
                    if (updates.mps !== undefined) target.mps = updates.mps;
                  });
                });
                const uploadResult = await api.uploadClientJson("FinData_Formulae.json", data);
                if (!uploadResult?.success) {
                  setAnalyticsMessage(uploadResult?.error || "Failed to save no. of shares / EPS / MPS");
                  return;
                }
                const result = await api.generateFinancialAnalytics();
                if (result?.success) {
                  setAnalyticsMessage(result.message || "Analytics generated successfully");
                  loadFinancialData();
                } else {
                  setAnalyticsMessage(result?.error || "Failed to generate analytics");
                }
              } catch (e) {
                console.error("Failed to generate analytics", e);
                setAnalyticsMessage(e instanceof Error ? e.message : "Failed to generate analytics");
              } finally {
                setAnalyticsLoading(false);
              }
            }}
          >
            {analyticsLoading ? "Generating Analytics..." : "Generate Analytics"}
          </Button>
        </div>
      </div>

      {/* Net Profit Ratio + scatter cards: Revenue vs Expenses, Profit vs MPS, Revenue vs MPS */}
      {(netProfitRatioChartData.length > 0 || revenueExpensesScatterData.length > 0 || profitMpsScatterData.length > 0 || revenueMpsScatterData.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {netProfitRatioChartData.length > 0 &&
            (() => {
              const latest = netProfitRatioChartData[netProfitRatioChartData.length - 1]?.value ?? 0;
              const previous = netProfitRatioChartData.length >= 2 ? (netProfitRatioChartData[netProfitRatioChartData.length - 2]?.value ?? 0) : 0;
              const trendUp = latest > previous;
              const trendColor = trendUp ? "#22c55e" : "#ef4444";
              return (
                <Card className="border-white/10 bg-white/5">
                  <CardHeader className="pb-1">
                    <CardTitle className="text-sm font-medium text-white">Net Profit Ratio</CardTitle>
                    <CardDescription className="text-xs text-white/60">
                      Profitability percentage (Net Profit / Revenue). Higher is better.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xl font-bold text-white">
                        {netProfitRatioChartData[netProfitRatioChartData.length - 1]?.value !== undefined
                          ? `${(latest * 100).toFixed(2)}%`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={netProfitRatioChartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                          <XAxis
                            dataKey="sortKey"
                            type="number"
                            domain={["dataMin", "dataMax"]}
                            ticks={netProfitRatioChartData.map((d) => d.sortKey)}
                            tickFormatter={(ts) =>
                              netProfitRatioChartData.find((d) => d.sortKey === ts)?.date ?? ""
                            }
                            fontSize={10}
                            stroke="rgba(255,255,255,0.6)"
                            tickLine={false}
                          />
                          <YAxis
                            fontSize={10}
                            stroke="rgba(255,255,255,0.6)"
                            tickLine={false}
                            tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                          />
                          <Tooltip
                            content={({ active, payload }) =>
                              active && payload?.length ? (
                                <div className="rounded-lg border border-white/20 bg-black/90 p-3 text-white shadow-xl">
                                  <p className="font-semibold text-sm">Net Profit Ratio</p>
                                  <p className="mb-1 text-xs text-white/60">
                                    {(payload[0]?.payload as { date?: string })?.date ?? ""}
                                  </p>
                                  <p className="text-base font-bold">
                                    {((payload[0]?.value ?? 0) * 100).toFixed(2)}%
                                  </p>
                                </div>
                              ) : null
                            }
                          />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke={trendColor}
                            strokeWidth={2}
                            dot={{ fill: trendColor, r: 3 }}
                            activeDot={{ r: 5, fill: trendColor }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          {revenueExpensesScatterData.length > 0 && (
            <Card className="border-white/10 bg-white/5">
              <CardHeader className="pb-1">
                <CardTitle className="text-sm font-medium text-white">Revenue vs Total Expenses</CardTitle>
                <CardDescription className="text-xs text-white/60">
                  Revenue (independent) vs Total Expenses (dependent) - Linear regression line.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {correlationRc != null && (
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white/80">
                      Correlation (analytics): <span className="font-semibold text-white">{typeof correlationRc === "number" ? correlationRc.toFixed(4) : correlationRc}</span>
                    </span>
                  </div>
                )}
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={revenueExpensesScatterData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis
                        dataKey="revenue"
                        fontSize={10}
                        stroke="rgba(255,255,255,0.6)"
                        tickLine={false}
                        tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`}
                      />
                      <YAxis
                        fontSize={10}
                        stroke="rgba(255,255,255,0.6)"
                        tickLine={false}
                        tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`}
                      />
                      <Tooltip
                        content={({ active, payload, label }) =>
                          active && payload?.length ? (
                            <div className="rounded-lg border border-white/20 bg-black/90 p-3 text-white shadow-xl">
                              <p className="font-semibold text-sm">Revenue vs Total Expenses</p>
                              <p className="mb-1 text-xs text-white/60">{(payload[0]?.payload as { date?: string })?.date ?? label}</p>
                              <p className="text-xs">Revenue: {formatCrores((payload[0]?.payload as { revenue?: number })?.revenue ?? 0)}</p>
                              <p className="text-xs">Total Expenses: {formatCrores((payload[0]?.payload as { expenses?: number })?.expenses ?? 0)}</p>
                            </div>
                          ) : null
                        }
                      />
                      <Scatter dataKey="expenses" fill="#3b82f6" fillOpacity={0.9} name="Revenue vs Expenses" />
                      <Line type="monotone" dataKey="regression" stroke="#22c55e" strokeWidth={2} dot={false} name="Linear regression" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
          {profitMpsScatterData.length > 0 && (
            <Card className="border-white/10 bg-white/5">
              <CardHeader className="pb-1">
                <CardTitle className="text-sm font-medium text-white">Profit/(loss) vs Market price per share</CardTitle>
                <CardDescription className="text-xs text-white/60">
                  Profit/(loss) for the period (X) vs Market price per share (Y) - Linear regression.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {correlationProfitMp != null && (
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white/80">
                      Correlation (analytics): <span className="font-semibold text-white">{typeof correlationProfitMp === "number" ? correlationProfitMp.toFixed(4) : correlationProfitMp}</span>
                    </span>
                  </div>
                )}
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={profitMpsScatterData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis
                        dataKey="profit"
                        fontSize={10}
                        stroke="rgba(255,255,255,0.6)"
                        tickLine={false}
                        tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`}
                      />
                      <YAxis
                        fontSize={10}
                        stroke="rgba(255,255,255,0.6)"
                        tickLine={false}
                        tickFormatter={(v) => `₹${v}`}
                      />
                      <Tooltip
                        content={({ active, payload }) =>
                          active && payload?.length ? (
                            <div className="rounded-lg border border-white/20 bg-black/90 p-3 text-white shadow-xl">
                              <p className="font-semibold text-sm">Profit vs MPS</p>
                              <p className="mb-1 text-xs text-white/60">{(payload[0]?.payload as { date?: string })?.date}</p>
                              <p className="text-xs">Profit: {formatCrores((payload[0]?.payload as { profit?: number })?.profit ?? 0)}</p>
                              <p className="text-xs">MPS: ₹{((payload[0]?.payload as { mps?: number })?.mps ?? 0).toLocaleString()}</p>
                            </div>
                          ) : null
                        }
                      />
                      <Scatter dataKey="mps" fill="#3b82f6" fillOpacity={0.9} name="Profit vs MPS" />
                      <Line type="monotone" dataKey="regression" stroke="#22c55e" strokeWidth={2} dot={false} name="Linear regression" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
          {revenueMpsScatterData.length > 0 && (
            <Card className="border-white/10 bg-white/5">
              <CardHeader className="pb-1">
                <CardTitle className="text-sm font-medium text-white">Revenue vs Market price per share</CardTitle>
                <CardDescription className="text-xs text-white/60">
                  Revenue (X) vs Market price per share (Y) - Linear regression.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {correlationRevenueMp != null && (
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white/80">
                      Correlation (analytics): <span className="font-semibold text-white">{typeof correlationRevenueMp === "number" ? correlationRevenueMp.toFixed(4) : correlationRevenueMp}</span>
                    </span>
                  </div>
                )}
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={revenueMpsScatterData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis
                        dataKey="revenue"
                        fontSize={10}
                        stroke="rgba(255,255,255,0.6)"
                        tickLine={false}
                        tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`}
                      />
                      <YAxis
                        fontSize={10}
                        stroke="rgba(255,255,255,0.6)"
                        tickLine={false}
                        tickFormatter={(v) => `₹${v}`}
                      />
                      <Tooltip
                        content={({ active, payload }) =>
                          active && payload?.length ? (
                            <div className="rounded-lg border border-white/20 bg-black/90 p-3 text-white shadow-xl">
                              <p className="font-semibold text-sm">Revenue vs MPS</p>
                              <p className="mb-1 text-xs text-white/60">{(payload[0]?.payload as { date?: string })?.date}</p>
                              <p className="text-xs">Revenue: {formatCrores((payload[0]?.payload as { revenue?: number })?.revenue ?? 0)}</p>
                              <p className="text-xs">MPS: ₹{((payload[0]?.payload as { mps?: number })?.mps ?? 0).toLocaleString()}</p>
                            </div>
                          ) : null
                        }
                      />
                      <Scatter dataKey="mps" fill="#3b82f6" fillOpacity={0.9} name="Revenue vs MPS" />
                      <Line type="monotone" dataKey="regression" stroke="#22c55e" strokeWidth={2} dot={false} name="Linear regression" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Formal P&L Statement */}
      <Card className="border-white/10 bg-white/5">
        <CardHeader>
          <CardTitle className="text-white">Profit & Loss Statement</CardTitle>
          <CardDescription className="text-white/60">
            Formal statement as per Indian accounting standards
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table
              className="table-fixed"
              style={{ minWidth: 180 + dateColumns.length * 160 + 80 + 80 }}
            >
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="sticky left-0 z-20 w-[180px] min-w-[180px] max-w-[180px] bg-[var(--card)] border-r border-white/10 font-medium text-white/60 whitespace-normal">
                    Particulars
                  </TableHead>
                  {dateColumns.map((col, i) => (
                    <TableHead
                      key={`${col.type}-${col.date}-${i}`}
                      className="font-semibold text-white text-center min-w-[160px] whitespace-nowrap border-white/10"
                    >
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-xs font-normal text-white/60">{col.type}</span>
                        <span>{col.date}</span>
                      </div>
                    </TableHead>
                  ))}
                  <TableHead className="sticky right-[80px] z-20 min-w-[80px] w-[80px] max-w-[80px] bg-[var(--card)] border-l border-white/10 font-medium text-white/60 text-right">
                    CAGR
                  </TableHead>
                  <TableHead className="sticky right-0 z-20 min-w-[80px] w-[80px] max-w-[80px] bg-[var(--card)] font-medium text-white/60 text-right">
                    Trend
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plData.map((item, index) => {
                  if (item.isSectionHeader) {
                    return (
                      <TableRow key={index} className="border-white/10 hover:bg-transparent">
                        <TableCell className="sticky left-0 z-10 w-[180px] max-w-[180px] font-semibold text-white bg-[var(--card)] border-r border-white/10 overflow-hidden whitespace-normal">
                        <span className="block break-words min-w-0">{item.label}</span>
                        </TableCell>
                        {dateColumns.map((col, i) => (
                          <TableCell key={`${col.type}-${col.date}-${i}`} className="min-w-[160px] border-white/10" />
                        ))}
                        <TableCell className="sticky right-[80px] z-10 bg-[var(--card)] border-l border-white/10" />
                        <TableCell className="sticky right-0 z-10 bg-[var(--card)]" />
                      </TableRow>
                    );
                  }

                  const cellClass = `py-1 ${item.isTotal ? "font-bold border-t border-white/10 pt-2 mt-2" : ""}`;
                  const labelClass = item.isTotal
                    ? "font-bold text-white"
                    : item.isSubItem
                    ? "text-sm text-white/90"
                    : "font-medium text-white";

                  return (
                    <TableRow key={index} className="border-white/10 hover:bg-transparent">
                      <TableCell
                        className={`sticky left-0 z-10 w-[180px] max-w-[180px] bg-[var(--card)] border-r border-white/10 ${cellClass} overflow-hidden whitespace-normal`}
                        style={{ paddingLeft: `${12 + item.level * 16}px` }}
                      >
                        <span className={`${labelClass} block break-words min-w-0`}>{item.label || "\u2014"}</span>
                      </TableCell>
                      {dateColumns.map((col, i) => {
                        const val = col.type === "QE" && item.valuesQe ? item.valuesQe[col.date] : item.values[col.date];
                        const show = val === 0 && !item.isTotal ? "—" : formatCrores(val);
                        return (
                          <TableCell
                            key={`${col.type}-${col.date}-${i}`}
                            className={`font-mono text-right min-w-[160px] text-white ${cellClass} ${item.isTotal ? "font-bold" : ""} border-white/10`}
                          >
                            {show}
                          </TableCell>
                        );
                      })}
                      <TableCell className={`sticky right-[80px] z-10 bg-[var(--card)] border-l border-white/10 text-right font-mono text-white ${cellClass}`}>
                        {item.cagr != null ? `${(item.cagr * 100).toFixed(2)}%` : "—"}
                      </TableCell>
                      <TableCell className={`sticky right-0 z-10 bg-[var(--card)] text-right text-white ${cellClass}`}>
                        <TrendSparkline
                          points={(() => {
                            const useQe = dateColumns.some((c) => c.type === "QE");
                            const src = useQe ? item.valuesQe : item.values;
                            if (!src) return undefined;
                            // Trend sparkline: always oldest→newest (chronological) regardless of column order
                            const chronologicalDates = sortDatesOldestFirst(dates);
                            return chronologicalDates.map((d) => src[d]).filter((v): v is number => typeof v === "number");
                          })()}
                          trend={item.trend}
                          isExpense={item.isExpense}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(() => {
                  const profitLossItem = plData.find((i) => i.label.includes("PROFIT/(LOSS) FOR THE PERIOD"));
                  const cellClass = "py-1 border-white/10";
                  const colKey = (col: DateColumn) => `${col.date}|${col.type}`;
                  return (
                    <>
                      <TableRow className="border-white/10 hover:bg-transparent border-t border-white/20">
                        <TableCell className={`sticky left-0 z-10 w-[180px] max-w-[180px] bg-[var(--card)] border-r border-white/10 font-medium text-white/90 ${cellClass} overflow-hidden whitespace-normal`}>
                          <span className="block break-words min-w-0">Number of Shares</span>
                        </TableCell>
                        {dateColumns.map((col, i) => (
                          <TableCell key={`${col.type}-${col.date}-${i}`} className={`min-w-[160px] ${cellClass}`}>
                            <Input
                              type="text"
                              inputMode="decimal"
                              className="h-8 w-full bg-white/10 border-white/20 text-white text-right font-mono text-sm"
                              placeholder="—"
                              value={noOfShares[colKey(col)] ?? ""}
                              onChange={(e) => setNoOfShares((prev) => ({ ...prev, [colKey(col)]: e.target.value }))}
                            />
                          </TableCell>
                        ))}
                        <TableCell className="sticky right-[80px] z-10 bg-[var(--card)] border-l border-white/10" />
                        <TableCell className="sticky right-0 z-10 bg-[var(--card)]" />
                      </TableRow>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableCell className={`sticky left-0 z-10 w-[180px] max-w-[180px] bg-[var(--card)] border-r border-white/10 font-medium text-white/90 ${cellClass} overflow-hidden whitespace-normal`}>
                          <span className="block break-words min-w-0">Earnings per Share</span>
                        </TableCell>
                        {dateColumns.map((col, i) => {
                          const key = colKey(col);
                          const profitVal = profitLossItem
                            ? col.type === "QE" && profitLossItem.valuesQe
                              ? profitLossItem.valuesQe[col.date]
                              : profitLossItem.values[col.date]
                            : 0;
                          const sharesNum = noOfShares[key] ? parseFloat(noOfShares[key]) : NaN;
                          const rawEps = !Number.isNaN(sharesNum) && sharesNum !== 0 && typeof profitVal === "number"
                            ? profitVal / sharesNum
                            : NaN;
                          const calculated = Number.isNaN(rawEps) || rawEps === 0 ? "" : rawEps.toFixed(2);
                          const displayVal = eps[key] !== undefined && eps[key] !== "" ? eps[key] : calculated;
                          return (
                            <TableCell key={`${col.type}-${col.date}-${i}`} className={`min-w-[160px] ${cellClass}`}>
                              <Input
                                type="text"
                                inputMode="decimal"
                                className="h-8 w-full bg-white/10 border-white/20 text-white text-right font-mono text-sm"
                                placeholder="—"
                                value={displayVal}
                                onChange={(e) => setEps((prev) => ({ ...prev, [key]: e.target.value }))}
                              />
                            </TableCell>
                          );
                        })}
                        <TableCell className="sticky right-[80px] z-10 bg-[var(--card)] border-l border-white/10" />
                        <TableCell className="sticky right-0 z-10 bg-[var(--card)]" />
                      </TableRow>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableCell className={`sticky left-0 z-10 w-[180px] max-w-[180px] bg-[var(--card)] border-r border-white/10 font-medium text-white/90 ${cellClass} overflow-hidden whitespace-normal`}>
                          <span className="block break-words min-w-0">Market Price per Share</span>
                        </TableCell>
                        {dateColumns.map((col, i) => (
                          <TableCell key={`${col.type}-${col.date}-${i}`} className={`min-w-[160px] ${cellClass}`}>
                            <Input
                              type="text"
                              inputMode="decimal"
                              className="h-8 w-full bg-white/10 border-white/20 text-white text-right font-mono text-sm"
                              placeholder="—"
                              value={mps[colKey(col)] ?? ""}
                              onChange={(e) => setMps((prev) => ({ ...prev, [colKey(col)]: e.target.value }))}
                            />
                          </TableCell>
                        ))}
                        <TableCell className="sticky right-[80px] z-10 bg-[var(--card)] border-l border-white/10" />
                        <TableCell className="sticky right-0 z-10 bg-[var(--card)]" />
                      </TableRow>
                    </>
                  );
                })()}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

